package com.example.event_management_system.ui.screens.settings

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.filled.Badge
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.Phone
import androidx.compose.material.icons.filled.SwapHoriz
import androidx.compose.material3.OutlinedTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.AssistChip
import androidx.compose.material3.AssistChipDefaults
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ListItem
import androidx.compose.material3.ListItemDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Snackbar
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.components.common.SmallLoadingIndicator
import com.example.event_management_system.ui.screens.location.LocationPickerScreen
import com.example.event_management_system.ui.screens.login.LoginScreen
import com.example.event_management_system.ui.theme.Dimens
import event_management_system.composeapp.generated.resources.Res
import event_management_system.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource

private fun initialsOf(displayName: String, email: String): String {
    val source = displayName.ifBlank { email.substringBefore('@') }
    val parts = source.trim().split(Regex("\\s+")).filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(2).uppercase()
        else -> (parts.first().first().toString() + parts.last().first().toString()).uppercase()
    }
}

class SettingsScreen : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = getScreenModel<SettingsScreenModel>()
        val state by screenModel.state.collectAsState()

        LaunchedEffect(state.isLoggedOut) {
            if (state.isLoggedOut) {
                navigator.replaceAll(LoginScreen())
            }
        }

        val snackbarHostState = remember { SnackbarHostState() }
        var showSignOutConfirm by remember { mutableStateOf(false) }

        LaunchedEffect(state.error) {
            val error = state.error
            if (error != null) {
                snackbarHostState.showSnackbar(error)
                screenModel.clearMessage()
            }
        }

        LaunchedEffect(state.message) {
            val msg = state.message
            if (msg != null) {
                snackbarHostState.showSnackbar(msg)
                screenModel.clearMessage()
            }
        }

        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text(stringResource(Res.string.screen_title_settings)) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        titleContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            },
            snackbarHost = { SnackbarHost(snackbarHostState) { data -> Snackbar(snackbarData = data) } }
        ) { padding ->
            if (state.isLoading) {
                LoadingIndicator()
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(padding)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = Dimens.paddingMedium, vertical = Dimens.paddingMedium)
                ) {
                    state.currentUser?.let { user ->
                        ProfileHeader(user)
                    }

                    Spacer(modifier = Modifier.height(Dimens.spacingXLarge))

                    state.currentUser?.let { user ->
                        SectionLabel(stringResource(Res.string.section_account))
                        Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                        ) {
                            Column {
                                DetailRow(
                                    icon = Icons.Filled.Badge,
                                    title = stringResource(Res.string.label_role),
                                    value = user.role.name.lowercase().replaceFirstChar { it.uppercase() }
                                )
                                HorizontalDivider()
                                DetailRow(
                                    icon = Icons.Filled.LocationOn,
                                    title = stringResource(Res.string.label_locations),
                                    value = stringResource(Res.string.label_locations_count, user.locationIds.size)
                                )
                            }
                        }

                        Spacer(modifier = Modifier.height(Dimens.spacingXLarge))

                        SectionLabel("Mobile Number")
                        Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                        MobileNumberEditor(
                            currentPhone = user.phone,
                            isSaving = state.isSavingPhone,
                            onSave = screenModel::updatePhone
                        )
                    }

                    Spacer(modifier = Modifier.height(Dimens.spacingXLarge))

                    SectionLabel(stringResource(Res.string.section_about))
                    Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                    ) {
                        DetailRow(
                            icon = Icons.Filled.Info,
                            title = stringResource(Res.string.app_name),
                            value = stringResource(Res.string.app_version)
                        )
                    }

                    Spacer(modifier = Modifier.height(Dimens.spacingXLarge))

                    TextButton(
                        onClick = { showSignOutConfirm = true },
                        enabled = !state.isLoggingOut,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        if (state.isLoggingOut) {
                            SmallLoadingIndicator()
                        } else {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.Logout,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.error
                            )
                            Spacer(modifier = Modifier.size(Dimens.spacingSmall))
                            Text(
                                text = stringResource(Res.string.action_sign_out),
                                color = MaterialTheme.colorScheme.error
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(Dimens.spacingLarge))
                }
            }
        }

        if (showSignOutConfirm) {
            AlertDialog(
                onDismissRequest = { showSignOutConfirm = false },
                icon = { Icon(Icons.AutoMirrored.Filled.Logout, contentDescription = null) },
                title = { Text("Sign out?") },
                text = { Text("You'll be returned to the sign-in screen.") },
                confirmButton = {
                    Button(
                        onClick = {
                            showSignOutConfirm = false
                            screenModel.signOut()
                        }
                    ) { Text(stringResource(Res.string.action_sign_out)) }
                },
                dismissButton = {
                    TextButton(onClick = { showSignOutConfirm = false }) { Text("Cancel") }
                }
            )
        }
    }
}

@Composable
private fun ProfileHeader(user: User) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(Dimens.paddingMedium),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(56.dp)
                    .clip(CircleShape)
                    .background(MaterialTheme.colorScheme.primary),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = initialsOf(user.displayName, user.email),
                    style = MaterialTheme.typography.titleLarge,
                    color = MaterialTheme.colorScheme.onPrimary,
                    fontWeight = FontWeight.SemiBold
                )
            }
            Spacer(modifier = Modifier.size(Dimens.spacingMedium))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = user.displayName.ifBlank { user.email.substringBefore('@') },
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(Dimens.spacingXxSmall))
                Text(
                    text = user.email,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                AssistChip(
                    onClick = {},
                    enabled = false,
                    label = { Text(user.role.name.lowercase().replaceFirstChar { it.uppercase() }) },
                    leadingIcon = { Icon(Icons.Filled.Badge, contentDescription = null) },
                    colors = AssistChipDefaults.assistChipColors(
                        disabledContainerColor = MaterialTheme.colorScheme.surface,
                        disabledLabelColor = MaterialTheme.colorScheme.onSurface,
                        disabledLeadingIconContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            }
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(
        text = text.uppercase(),
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(horizontal = Dimens.paddingSmall)
    )
}

@Composable
private fun DetailRow(
    icon: ImageVector,
    title: String,
    value: String,
    trailing: (@Composable () -> Unit)? = null
) {
    ListItem(
        headlineContent = { Text(title) },
        supportingContent = { Text(value) },
        leadingContent = {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary
            )
        },
        trailingContent = trailing,
        colors = ListItemDefaults.colors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
    )
}

@Composable
private fun MobileNumberEditor(
    currentPhone: String?,
    isSaving: Boolean,
    onSave: (String) -> Unit
) {
    var phoneInput by remember(currentPhone) { mutableStateOf(currentPhone.orEmpty()) }
    val isDirty = phoneInput.trim() != currentPhone.orEmpty().trim()

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
    ) {
        Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
            OutlinedTextField(
                value = phoneInput,
                onValueChange = { phoneInput = it },
                label = { Text("Mobile Number") },
                leadingIcon = { Icon(Icons.Filled.Phone, contentDescription = null) },
                placeholder = { Text("e.g. +1 555 123 4567") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Phone,
                    imeAction = ImeAction.Done
                ),
                keyboardActions = KeyboardActions(onDone = {
                    if (isDirty && !isSaving) onSave(phoneInput)
                }),
                enabled = !isSaving
            )
            if (isDirty) {
                Spacer(modifier = Modifier.height(Dimens.spacingSmall))
                Button(
                    onClick = { onSave(phoneInput) },
                    enabled = !isSaving,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    if (isSaving) SmallLoadingIndicator()
                    else Text("Save Mobile Number")
                }
            }
        }
    }
}
